/**
 * One crawl-status vocabulary for every Setup surface (SS001-8).
 *
 * Setup shows the same domain in two cards: **Prep all domains** (which read the
 * live crawl status) and **Competitors** (which used to infer state from
 * analyzed-page counts). Analyzed pages only land at the end of a crawl, so a
 * domain that was actively crawling showed **Crawling** in one card and
 * **Not crawled** in the other — the operator could not tell whether anything
 * was running.
 *
 * Both cards now resolve their chip through this module: the live crawl status
 * is the truth, the backend's `crawlState` is the fallback until it loads, and
 * analyzed-page counts only describe what happened *after* the crawl.
 */

export type SetupCrawlPhase =
  | 'unknown'
  | 'not_crawled'
  | 'starting'
  | 'crawling'
  | 'analyzing'
  | 'complete'
  | 'failed';

export interface SetupStatusChip {
  label: string;
  cls: string;
}

/** The subset of `CrawlStatus` that decides the phase. */
export interface LiveCrawlStatusLike {
  phase?: 'crawling' | 'analyzing' | 'complete' | 'idle';
  isComplete?: boolean;
  pagesCrawled?: number;
}

/** Backend-reported crawl state (competitor readiness), used until live status loads. */
export type ReportedCrawlState = 'none' | 'crawling' | 'analyzing' | 'complete' | 'failed';

export interface SetupCrawlPhaseInput {
  /** A start request is in flight from this UI. */
  starting?: boolean;
  /** Live `GET /domains/:domain/crawl/status`, or null when not loaded yet. */
  live?: LiveCrawlStatusLike | null;
  /** Fallback from `GET /domains/:domain/competitors/status`. */
  reported?: ReportedCrawlState | null;
}

/**
 * Resolve the single crawl phase for a domain. Live status wins; the reported
 * state only fills the gap before the per-domain status request returns.
 */
export function resolveSetupCrawlPhase(input: SetupCrawlPhaseInput): SetupCrawlPhase {
  if (input.starting) return 'starting';

  const live = input.live;
  if (live) {
    if (live.isComplete || live.phase === 'complete') return 'complete';
    if (live.phase === 'analyzing') return 'analyzing';
    if (live.phase === 'crawling') return 'crawling';
    if ((live.pagesCrawled ?? 0) > 0) return 'crawling';
    return 'not_crawled';
  }

  switch (input.reported) {
    case 'crawling':
      return 'crawling';
    case 'analyzing':
      return 'analyzing';
    case 'complete':
      return 'complete';
    case 'failed':
      return 'failed';
    case 'none':
      return 'not_crawled';
    default:
      return 'unknown';
  }
}

export function setupCrawlChip(phase: SetupCrawlPhase): SetupStatusChip {
  switch (phase) {
    case 'starting':
      return { label: 'Starting…', cls: 'state-running' };
    case 'crawling':
      return { label: 'Crawling', cls: 'state-running' };
    case 'analyzing':
      return { label: 'Analyzing', cls: 'state-running' };
    case 'complete':
      return { label: 'Complete', cls: 'state-complete' };
    case 'failed':
      return { label: 'Crawl failed', cls: 'state-blocked' };
    case 'not_crawled':
      return { label: 'Not crawled', cls: 'state-not_started' };
    default:
      return { label: 'Checking…', cls: 'state-waiting' };
  }
}

/** True once the crawl (and its analysis) has finished for this domain. */
export function isSetupCrawlDone(phase: SetupCrawlPhase): boolean {
  return phase === 'complete';
}

export interface CompetitorChipInput {
  crawlPhase: SetupCrawlPhase;
  activeJob: 'queued' | 'running' | null;
  ready: boolean;
  hasTopicSignals: boolean;
  pagesAnalyzed: number;
  pagesWithContent?: number;
}

/**
 * Competitor chip = crawl truth first, prep state second. Anything before the
 * crawl finishes renders exactly the label the Prep list shows for that domain;
 * only once the crawl is done does topic-prep readiness take over.
 */
export function setupCompetitorChip(input: CompetitorChipInput): SetupStatusChip {
  if (input.activeJob === 'running') return { label: 'Prepping…', cls: 'state-running' };
  if (input.activeJob === 'queued') return { label: 'Queued', cls: 'state-queued' };

  if (!isSetupCrawlDone(input.crawlPhase)) {
    return setupCrawlChip(input.crawlPhase);
  }

  if (input.ready) return { label: 'Ready', cls: 'state-complete' };
  // Crawl record says done but nothing was analyzed — a real dead end, and a
  // different problem from "analyzed but no text captured" below.
  if (input.pagesAnalyzed <= 0) return { label: 'No pages analyzed', cls: 'state-blocked' };
  if ((input.pagesWithContent ?? 0) === 0) return { label: 'No content captured', cls: 'state-blocked' };
  if (!input.hasTopicSignals) return { label: 'Extracting topics', cls: 'state-waiting' };
  return { label: 'Needs topic prep', cls: 'state-not_started' };
}
